import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, Mail, Phone, Loader2, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/motion";
import { PhotoSlot } from "@/components/site/PhotoSlot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = ["Corn DDGS", "Rice DDGS", "Tea", "Spices", "Other"];

const EMPTY = { name: "", company: "", email: "", phone: "", product: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — your inquiry has reached our team.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "border-0 border-b border-[#DBCFA0] rounded-none bg-transparent px-0 text-[#3D2817] text-base placeholder:text-[#6B4423]/50 focus-visible:ring-0 focus-visible:border-[#3D2817] transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F0E6C0] py-24 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-y-16 lg:gap-x-20">
          {/* Left: info */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <p className="mb-5 text-xs md:text-sm uppercase tracking-[0.25em] text-[#7A4E24]">
                &#8212; Get in Touch
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-[#3D2817]">
                Let&apos;s move your
                <span className="block italic text-[#7A4E24]">next consignment.</span>
              </h2>
              <p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-[#6B4423]">
                Tell us your requirement and volumes. Our team responds with
                specifications, availability and pricing.
              </p>

              <div className="mt-12 space-y-7">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin size={20} className="mt-1 shrink-0 text-[#7A4E24]" />
                  <p className="text-base leading-relaxed text-[#3D2817]">
                    Agro Trade Global LLP<br />
                    House No 21 B, P.B. Road, Rehabari,<br />
                    Guwahati, Assam &ndash; 781008, India
                  </p>
                </div>
                <a
                  href="mailto:enquiry@agrotradeglobal.in"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 text-[#3D2817] transition-colors hover:text-[#7A4E24]"
                >
                  <Mail size={20} className="shrink-0 text-[#7A4E24]" />
                  <span className="text-base">enquiry@agrotradeglobal.in</span>
                </a>
                <a
                  href="tel:+918638241548"
                  className="flex items-center gap-4 text-[#3D2817] transition-colors hover:text-[#7A4E24]"
                >
                  <Phone size={20} className="shrink-0 text-[#7A4E24]" />
                  <span className="text-base">+91 86382 41548</span>
                </a>
              </div>

              <div className="mt-12">
                {IMAGES.facility ? (
                  <div className="overflow-hidden rounded-2xl border border-[#3D2817]">
                    <img
                      src={IMAGES.facility}
                      alt="Agro Trade Global operations"
                      className="h-48 w-full object-cover"
                    />
                  </div>
                ) : (
                  <PhotoSlot
                    testId="contact-photo-slot"
                    caption="Operations"
                    label="Photo slot"
                    height="h-48"
                  />
                )}
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            data-testid="inquiry-form"
            className="col-span-12 lg:col-span-7 lg:pt-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-[#6B4423]">
                  Name *
                </Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="company" className="text-xs uppercase tracking-[0.15em] text-[#6B4423]">
                  Company
                </Label>
                <Input
                  id="company"
                  data-testid="input-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Company / organization"
                  className={inputCls}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-[#6B4423]">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.15em] text-[#6B4423]">
                  Phone
                </Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                  className={inputCls}
                />
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#6B4423]">
                  Product Interest
                </Label>
                <Select value={form.product} onValueChange={update("product")}>
                  <SelectTrigger
                    data-testid="select-product"
                    className="border-0 border-b border-[#DBCFA0] rounded-none bg-transparent px-0 text-[#3D2817] focus:ring-0 focus:border-[#3D2817]"
                  >
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F5F2E9] border-[#3D2817] text-[#3D2817]">
                    {PRODUCTS.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`product-option-${p.split(" ")[0].toLowerCase()}`}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-[0.15em] text-[#6B4423]">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  data-testid="input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us your requirement, grade and monthly volume..."
                  rows={5}
                  className="border border-[#DBCFA0] rounded-lg bg-transparent text-[#3D2817] text-base placeholder:text-[#6B4423]/50 focus-visible:ring-0 focus-visible:border-[#3D2817] transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="submit-inquiry"
              disabled={submitting}
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#3D2817] px-9 py-4 text-sm uppercase tracking-[0.18em] text-[#F0E6C0] transition-transform duration-300 hover:scale-[0.97] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send Inquiry
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
